import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Trash2, Mail, User, Shield, AlertTriangle, Edit,
  Users, Lock, Crown, Zap, Star, ChevronRight, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import api from '@/utils/api';
import useApiData from '@/hooks/useApiData';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorMessage from '@/components/ui/ErrorMessage';
import EmptyState from '@/components/ui/EmptyState';

// ─── Limites por plano (espelho do backend) ───
const PLAN_LIMITS = {
  basico:        { max_users: 3,  label: 'Básico',        color: 'slate',   icon: Star },
  intermediario: { max_users: 10, label: 'Intermediário',  color: 'blue',    icon: Zap },
  full:          { max_users: 20, label: 'Full',           color: 'violet',  icon: Crown },
};

function getPlanLimit(plan) {
  return PLAN_LIMITS[plan] || PLAN_LIMITS['basico'];
}

// ─── Barra de uso de usuários ───
function UserQuotaBar({ used, max, plan }) {
  const pct = Math.min((used / max) * 100, 100);
  const isNearLimit = pct >= 80;
  const atLimit = pct >= 100;

  const limit = getPlanLimit(plan);
  const PlanIcon = limit.icon;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
            atLimit ? 'bg-red-100 text-red-600' :
            isNearLimit ? 'bg-amber-100 text-amber-600' :
            'bg-violet-100 text-violet-600'
          }`}>
            <Users className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900">Usuários da Equipe</p>
            <p className="text-xs text-slate-500">
              Plano <strong className={`text-${limit.color}-600`}>{limit.label}</strong>
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className={`text-2xl font-black ${atLimit ? 'text-red-600' : isNearLimit ? 'text-amber-600' : 'text-slate-900'}`}>
            {used} <span className="text-slate-400 font-normal text-lg">/ {max}</span>
          </p>
          <p className="text-xs text-slate-400">usuários ativos</p>
        </div>
      </div>

      {/* Barra de progresso */}
      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            atLimit ? 'bg-red-500' :
            isNearLimit ? 'bg-amber-500' :
            'bg-violet-600'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className={`text-xs font-semibold ${
          atLimit ? 'text-red-600' : isNearLimit ? 'text-amber-600' : 'text-slate-400'
        }`}>
          {atLimit
            ? '⚠️ Limite atingido — faça upgrade para adicionar mais'
            : isNearLimit
            ? `Atenção: apenas ${max - used} vaga(s) restante(s)`
            : `${max - used} vaga(s) disponíve(is) neste plano`}
        </p>
        <p className="text-xs text-slate-400">{pct.toFixed(0)}% usado</p>
      </div>

      {/* Upgrade CTA quando no limite */}
      {atLimit && (
        <div className="mt-3 flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <Info className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-700 font-medium">
            Seu plano {limit.label} permite até {max} usuários.
            {plan !== 'full' && ' Faça upgrade para adicionar mais membros à equipe.'}
          </p>
          {plan !== 'full' && (
            <button
              className="ml-auto flex-shrink-0 text-xs font-bold text-amber-700 hover:text-amber-900 flex items-center gap-1"
              onClick={() => window.location.href = '/assinaturas'}
            >
              Ver planos <ChevronRight className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Cards de planos ───
function PlanComparisonCards({ currentPlan }) {
  const plans = [
    { key: 'basico', label: 'Básico', users: 3, icon: Star, color: 'slate' },
    { key: 'intermediario', label: 'Intermediário', users: 10, icon: Zap, color: 'blue' },
    { key: 'full', label: 'Full', users: 20, icon: Crown, color: 'violet' },
  ];

  return (
    <div className="grid grid-cols-3 gap-3">
      {plans.map(p => {
        const isActive = currentPlan === p.key;
        const PIcon = p.icon;
        return (
          <div key={p.key} className={`rounded-xl border p-4 text-center transition-all ${
            isActive
              ? `border-${p.color}-300 bg-${p.color}-50`
              : 'border-slate-100 bg-white opacity-60'
          }`}>
            <div className={`h-8 w-8 rounded-lg mx-auto mb-2 flex items-center justify-center ${
              isActive ? `bg-${p.color}-100 text-${p.color}-600` : 'bg-slate-100 text-slate-400'
            }`}>
              <PIcon className="h-4 w-4" />
            </div>
            <p className={`text-xs font-bold ${isActive ? `text-${p.color}-700` : 'text-slate-500'}`}>
              {p.label}
            </p>
            <p className={`text-xl font-black mt-1 ${isActive ? 'text-slate-900' : 'text-slate-400'}`}>
              {p.users}
            </p>
            <p className="text-[10px] text-slate-400">usuários</p>
            {isActive && (
              <span className={`mt-2 inline-block text-[10px] font-bold px-2 py-0.5 rounded-full bg-${p.color}-100 text-${p.color}-700`}>
                Seu plano
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Equipe() {
  const { user, company } = useAuth();

  const isAdmin = ['admin', 'super_admin', 'gestor'].includes(user?.role);
  const canManage = ['admin', 'super_admin'].includes(user?.role);

  const { data: usersData, isLoading, error, refresh: refreshUsers } = useApiData(isAdmin ? '/admin/users' : null);
  const users = usersData || [];

  const plan = company?.plan || 'basico';
  const limitByPlan = getPlanLimit(plan).max_users;
  const maxUsers = limitByPlan;
  const atLimit = users.length >= maxUsers;

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'vendedor' });
  const [submitting, setSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', role: 'vendedor' });
    setEditingUser(null);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (atLimit) {
      toast.error(`Limite de ${maxUsers} usuários atingido para o plano ${plan}.`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/admin/users', formData);
      toast.success('Membro adicionado com sucesso!');
      setShowForm(false);
      resetForm();
      refreshUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { name: formData.name, role: formData.role };
      if (formData.password?.trim()) payload.password = formData.password;
      await api.put(`/admin/users/${editingUser.id}`, payload);
      toast.success('Usuário atualizado!');
      resetForm();
      refreshUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar.');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (u) => {
    setShowForm(false);
    setEditingUser(u);
    setFormData({ name: u.name, email: u.email, password: '', role: u.role });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Certeza que deseja remover este acesso?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success('Acesso revogado.');
      refreshUsers();
    } catch (error) {
      toast.error('Erro ao remover usuário.');
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <AlertTriangle className="h-12 w-12 text-yellow-500" />
        <h2 className="text-xl font-bold">Acesso Restrito</h2>
        <p className="text-slate-500 text-center max-w-sm">Apenas administradores podem gerenciar a equipe.</p>
      </div>
    );
  }

  const roleStyles = {
    vendedor: 'bg-blue-50 text-blue-700 border-blue-200',
    desenvolvedor: 'bg-purple-50 text-purple-700 border-purple-200',
    operador: 'bg-slate-50 text-slate-700 border-slate-200',
    financeiro: 'bg-orange-50 text-orange-700 border-orange-200',
    gestor: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    admin: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    super_admin: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };

  const roleLabels = {
    vendedor: 'Vendedor',
    desenvolvedor: 'Dev',
    operador: 'Operador',
    financeiro: 'Financeiro',
    gestor: 'Gestor',
    admin: 'Admin',
    super_admin: 'Super Admin'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Gestão de Equipe</h1>
          <p className="text-slate-500 text-sm mt-1">Gerencie os membros da sua agência e suas permissões de acesso</p>
        </div>
        {canManage && (
          <Button
            onClick={() => { 
              if (atLimit) {
                toast.error(`Limite do plano atingido (${maxUsers} usuários). Faça upgrade para continuar.`);
                return;
              }
              setShowForm(!showForm);
              resetForm();
            }}
            size="sm"
            className={`gap-1.5 ${atLimit ? 'opacity-60 cursor-not-allowed' : ''}`}
            disabled={atLimit && !showForm}
          >
            {showForm ? 'Fechar' : <><Plus className="h-4 w-4" /> Novo Membro</>}
          </Button>
        )}
      </div>

      {/* Quota bar + Plan cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <UserQuotaBar used={users.length} max={maxUsers} plan={plan} />
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Limites por Plano</p>
          <PlanComparisonCards currentPlan={plan} />
        </div>
      </div>

      {/* Form inline */}
      {showForm && !atLimit && (
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <Plus className="h-4 w-4 text-violet-600" /> Novo Colaborador
          </h3>
          <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="name" className="text-xs">Nome</Label>
              <Input id="name" required value={formData.name} onChange={handleInputChange} className="h-9 mt-1"/>
            </div>
            <div>
              <Label htmlFor="email" className="text-xs">E-mail</Label>
              <Input id="email" type="email" required value={formData.email} onChange={handleInputChange} className="h-9 mt-1"/>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs">Senha Provisória</Label>
              <Input id="password" type="password" required value={formData.password} onChange={handleInputChange} className="h-9 mt-1" minLength={6}/>
            </div>
            <div>
              <Label htmlFor="role" className="text-xs">Permissão</Label>
              <select id="role" className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm mt-1" value={formData.role} onChange={handleInputChange}>
                <option value="vendedor">Vendedor</option>
                <option value="desenvolvedor">Desenvolvedor</option>
                <option value="operador">Operador</option>
                <option value="financeiro">Financeiro</option>
                <option value="gestor">Gestor</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <div className="md:col-span-2 flex justify-end mt-2">
              <Button type="submit" disabled={submitting} size="sm">
                {submitting ? 'Salvando...' : 'Criar Membro'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={!!editingUser} onOpenChange={(open) => { if(!open) resetForm() }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Membro</DialogTitle>
            <DialogDescription>Altere permissões e dados do usuário.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Nome</Label>
              <Input id="name" required value={formData.name} onChange={handleInputChange} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">E-mail (fixo)</Label>
              <Input disabled value={formData.email} className="h-9 bg-slate-50" />
            </div>
            <div>
              <Label className="text-xs">Permissão</Label>
              <select id="role" className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm" value={formData.role} onChange={handleInputChange}>
                <option value="vendedor">Vendedor</option>
                <option value="desenvolvedor">Desenvolvedor</option>
                <option value="operador">Operador</option>
                <option value="financeiro">Financeiro</option>
                <option value="gestor">Gestor</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Nova Senha (opcional)</Label>
              <Input id="password" type="password" value={formData.password} onChange={handleInputChange} placeholder="Manter atual" className="h-9" minLength={6}/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" onClick={resetForm}>Cancelar</Button>
              <Button type="submit" disabled={submitting} size="sm">{submitting ? 'Salvando...' : 'Salvar'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tabela */}
      {isLoading ? (
        <LoadingSpinner message="Carregando equipe..." />
      ) : error ? (
        <ErrorMessage message={error} onRetry={refreshUsers} />
      ) : users.length === 0 ? (
        <EmptyState title="Nenhum membro na equipe" description="Adicione o primeiro colaborador clicando em 'Novo Membro'." />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="h-10 px-5 text-xs font-medium text-slate-500 uppercase tracking-wider">Membro</th>
                <th className="h-10 px-5 text-xs font-medium text-slate-500 uppercase tracking-wider">Cargo</th>
                <th className="h-10 px-5 text-xs font-medium text-slate-500 uppercase tracking-wider">Desde</th>
                <th className="h-10 px-5 text-xs font-medium text-slate-500 uppercase tracking-wider w-24">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-slate-50/70 transition-colors">
                  <td className="p-3 px-5">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                        {u.role?.includes('admin') ? <Shield className="h-4 w-4 text-emerald-600" /> : <User className="h-4 w-4 text-slate-500" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{u.name}</p>
                        <p className="text-[11px] text-slate-400 flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 px-5">
                    <Badge variant="outline" className={`text-xs ${roleStyles[u.role] || ''}`}>
                      {roleLabels[u.role] || u.role || 'N/A'}
                    </Badge>
                  </td>
                  <td className="p-3 px-5 text-xs text-slate-500">
                    {new Date(u.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="p-3 px-5">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-primary" onClick={() => openEdit(u)}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      {u.id !== user?.id && canManage && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-red-600" onClick={() => handleDelete(u.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
